import React, { useState, useEffect } from 'react';
import './index.scss';
import { useDispatch } from 'react-redux';
import { RotatingSquare } from 'react-loader-spinner';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { icons } from '../../../assets/icons';
import { showToast } from '../../../components/modal';
import { v4 as uuidv4 } from 'uuid';
import logoImage from '../../../assets/jeunessee.png';
import { registerClient } from '../../../api/redux/slices/Customer';
import { TunisiaCities } from '../../../data';
import PageWrapper from '../../../components/PageWrapper';

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [load, setLoad] = useState(false);
    const [activeSection, setActiveSection] = useState('account');
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [shopToDeleteIndex, setShopToDeleteIndex] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        fetch('https://cdn.jsdelivr.net/npm/world-countries@4/dist/countries.json')
            .then((res) => res.json())
            .then((data) => {
                const sortedCountries = data
                    .map((country) => ({
                        name: country?.name?.common,
                        code: country?.cca3,
                        flag: country?.flag,
                    }))
                    .filter((country) => country.name && country.code)
                    .sort((a, b) => a.name.localeCompare(b.name));
                setCountries(sortedCountries);
            })
            .catch((err) => console.error('Error fetching countries:', err));
    }, []);

    const [values, setValues] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company_name: '',
        tax_registration_number: '',
        customs_code: '',
        company_code: '',
        password: '',
        confirmPassword: '',
        regulation: 'espece',
        categories: 'detaillant',
    });

    const [shopFields, setShopFields] = useState([
        {
            id: uuidv4(),
            name: '',
            address_number: '',
            street: '',
            ville: '',
            postal_code: '',
            country: '',
            region: '',
        },
    ]);

    const [errors, setErrors] = useState({});

    const clearFieldError = (fieldName) => {
        if (errors[fieldName]) {
            setErrors(prev => ({ ...prev, [fieldName]: '' }));
        }
    };

    const validateStep1 = () => {
        const newErrors = {};

        if (!values.first_name) newErrors.first_name = 'Le nom est requis';
        if (!values.last_name) newErrors.last_name = 'Le prénom est requis';
        if (!values.email) newErrors.email = 'Email est requis';
        if (!values.phone) newErrors.phone = 'Le numéro de téléphone est requis';
        if (!values.company_name) newErrors.company_name = 'Le nom de la société est requis';
        if (!values.tax_registration_number) newErrors.tax_registration_number = 'Le matricule fiscal est requis'; // ✅ Added
        if (!values.password) newErrors.password = 'Le mot de passe est requis';
        if (values.password && values.password.length < 8) newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
        if (!values.confirmPassword) newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
        if (values.password !== values.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateForm = () => {
        const newErrors = {};

        if (!values.first_name) newErrors.first_name = 'Le nom est requis';
        if (!values.last_name) newErrors.last_name = 'Le prénom est requis';
        if (!values.email) newErrors.email = 'Email est requis';
        if (!values.phone) newErrors.phone = 'Le numéro de téléphone est requis';
        if (!values.company_name) newErrors.company_name = 'Le nom de la société est requis';
        if (!values.tax_registration_number) newErrors.tax_registration_number = 'Le matricule fiscal est requis'; // ✅ Added
        if (!values.password) newErrors.password = 'Le mot de passe est requis';
        if (values.password && values.password.length < 8) newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
        if (!values.confirmPassword) newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
        if (values.password !== values.confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';

        shopFields.forEach((shop, index) => {
            if (!shop.name) newErrors[`shop_${index}_name`] = 'Nom du magasin requis';
            if (!shop.address_number) newErrors[`shop_${index}_address_number`] = 'Numéro requis';
            if (!shop.street) newErrors[`shop_${index}_street`] = 'Rue requise';
            if (!shop.ville) newErrors[`shop_${index}_ville`] = 'Ville requise';
            if (!shop.postal_code) newErrors[`shop_${index}_postal_code`] = 'Code postal requis';
            if (!shop.country) newErrors[`shop_${index}_country`] = 'Pays requis';
            if (shop.country === 'TUN' && !shop.region) newErrors[`shop_${index}_region`] = 'Gouvernorat requis';
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const addField = () => {
        setShopFields([
            ...shopFields,
            {
                id: uuidv4(),
                name: '',
                address_number: '',
                street: '',
                ville: '',
                postal_code: '',
                country: '',
                region: '',
            },
        ]);
    };

    const handleChangeShopForm = (id, e) => {
        const { name, value } = e.target;
        let data = [...shopFields];
        const index = data.findIndex((item) => item.id === id);

        if (index !== -1) {
            if (name === 'postal_code') {
                data[index][name] = value.replace(/\D/g, '');
            } else if (name === 'country') {
                data[index][name] = value;
                // Reset region if country is not Tunisia
                if (value !== 'TUN') {
                    data[index].region = '';
                }
            } else {
                data[index][name] = value;
            }
            setShopFields(data);
        }

        clearFieldError(`shop_${index}_${name}`);
    };

    const handleDeleteAddedShop = (id) => {
        if (shopFields.length > 1) {
            setShopFields(shopFields.filter((field) => field.id !== id));
            setShowDeleteModal(false);
            setShopToDeleteIndex(null);
        }
    };

    const confirmDeleteShop = (id) => {
        setShopToDeleteIndex(id);
        setShowDeleteModal(true);
    };

    const handleInputChange = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
        clearFieldError(field);
    };

    const handleContinue = () => {
        if (!validateStep1()) {
            showToast('Veuillez remplir tous les champs obligatoires.', 'warn');
            return;
        }
        setActiveSection('shops');
    };

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('Veuillez remplir tous les champs correctement', 'warn');
            return;
        }

        setLoad(true);

        const trimmedShopFields = shopFields.map((item) => ({
            name: item.name.trim(),
            address_number: item.address_number,
            street: item.street.trim(),
            ville: item.ville.trim(),
            postal_code: item.postal_code,
            country: item.country,
            region: item.region || '',
        }));

        const userData = {
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            email: values.email.trim(),
            phone: values.phone.trim(),
            company_name: values.company_name.trim(),
            tax_registration_number: values.tax_registration_number.trim(), // ✅ Now required, no longer optional
            customs_code: values.customs_code.trim() || undefined,
            company_code: values.company_code.trim(),
            password: values.password,
            confirmPassword: values.confirmPassword,
            shop: trimmedShopFields,
            regulation: values.regulation,
            categories: values.categories,
        };

        try {
            await dispatch(registerClient(userData)).unwrap();
            toast.success('Inscription réussie ! Veuillez vous connecter.', {
                position: 'top-center',
                autoClose: 2000,
            });
            navigate('/login');
        } catch (err) {
            const errorMessage = err?.message || err?.response?.data?.message || "Erreur lors de l'inscription";
            showToast(errorMessage, 'error');
        } finally {
            setLoad(false);
        }
    };

    const togglePassword = () => setPasswordVisible(!passwordVisible);
    const toggleConfirmPassword = () => setConfirmPasswordVisible(!confirmPasswordVisible);

    return (
        <div className="jeuness-register">
            <div className="jeuness-register-container">
                {/* LEFT SIDE - Brand Section */}
                <div className="jeuness-register-brand">
                    <div className="brand-content">
                        <Link to="/" className="brand-logo">
                            <img src={logoImage} alt="JEUN'ESS" />
                            <span>JEUN'ESS</span>
                        </Link>

                        <div className="brand-quote">
                            <div className="quote-icon">🌱</div>
                            <h2>Commencez votre<br />aventure avec nous</h2>
                            <p>
                                Rejoignez la marketplace collaborative qui transforme
                                la façon dont les entreprises commandent et collaborent.
                            </p>
                        </div>
                    </div>

                    <div className="brand-decor">
                        <div className="decor-circle decor-1"></div>
                        <div className="decor-circle decor-2"></div>
                        <div className="decor-circle decor-3"></div>
                        <div className="decor-wave"></div>
                    </div>
                </div>

                {/* RIGHT SIDE - Form Section */}
                <div className="jeuness-register-form">
                    <div className="form-container">
                        <div className="register-header">
                            <div className="header-badge">✨ Nouvelle inscription</div>
                            <h2>Créer un compte</h2>
                            <p>Remplissez vos informations pour commencer</p>
                        </div>

                        <div className="progress-stepper">
                            <div className={`step ${activeSection === 'account' ? 'active' : 'completed'}`}>
                                <div className="step-circle">1</div>
                                <div className="step-label">Informations</div>
                            </div>
                            <div className="step-line"></div>
                            <div className={`step ${activeSection === 'shops' ? 'active' : ''}`}>
                                <div className="step-circle">2</div>
                                <div className="step-label">Magasins</div>
                            </div>
                        </div>

                        <form onSubmit={onSubmit}>
                            {activeSection === 'account' && (
                                <div className="form-content-scroll">
                                    {/* Identité */}
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>👤</span>
                                            <h3>Identité</h3>
                                        </div>
                                        <div className="input-group">
                                            <div className="input-row-half">
                                                <div className="input-field-wrapper">
                                                    <label>Nom <span className="required">*</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="Votre nom"
                                                        value={values.first_name}
                                                        onChange={(e) => handleInputChange('first_name', e.target.value)}
                                                    />
                                                    {errors.first_name && <span className="error-message">{errors.first_name}</span>}
                                                </div>
                                                <div className="input-field-wrapper">
                                                    <label>Prénom <span className="required">*</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="Votre prénom"
                                                        value={values.last_name}
                                                        onChange={(e) => handleInputChange('last_name', e.target.value)}
                                                    />
                                                    {errors.last_name && <span className="error-message">{errors.last_name}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Contact */}
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>📧</span>
                                            <h3>Contact</h3>
                                        </div>
                                        <div className="input-group">
                                            <div className="input-field-wrapper">
                                                <label>Email <span className="required">*</span></label>
                                                <input
                                                    type="email"
                                                    placeholder="Email professionnel"
                                                    value={values.email}
                                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                                />
                                                {errors.email && <span className="error-message">{errors.email}</span>}
                                            </div>
                                            <div className="input-field-wrapper">
                                                <label>Téléphone <span className="required">*</span></label>
                                                <input
                                                    type="tel"
                                                    placeholder="Téléphone"
                                                    value={values.phone}
                                                    onChange={(e) => handleInputChange('phone', e.target.value.replace(/[^0-9+\s]/g, ''))}
                                                />
                                                {errors.phone && <span className="error-message">{errors.phone}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Entreprise */}
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>🏢</span>
                                            <h3>Entreprise</h3>
                                        </div>
                                        <div className="input-group">
                                            <div className="input-field-wrapper">
                                                <label>Nom de la société <span className="required">*</span></label>
                                                <input
                                                    type="text"
                                                    placeholder="Nom de la société"
                                                    value={values.company_name}
                                                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                                                />
                                                {errors.company_name && <span className="error-message">{errors.company_name}</span>}
                                            </div>
                                            <div className="input-row-half">
                                                <div className="input-field-wrapper">
                                                    <label>Matricule fiscal <span className="required">*</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="Matricule fiscal"
                                                        value={values.tax_registration_number}
                                                        onChange={(e) => handleInputChange('tax_registration_number', e.target.value)}
                                                    />
                                                    {errors.tax_registration_number && <span className="error-message">{errors.tax_registration_number}</span>}
                                                </div>
                                                <div className="input-field-wrapper">
                                                    <label>Code douane (optionnel)</label>
                                                    <input
                                                        type="text"
                                                        placeholder="Code douane (optionnel)"
                                                        value={values.customs_code}
                                                        onChange={(e) => handleInputChange('customs_code', e.target.value)}
                                                    />
                                                    {errors.customs_code && <span className="error-message">{errors.customs_code}</span>}
                                                </div>
                                            </div>
                                            <div className="input-field-wrapper">
                                                <label>Code parrainage (optionnel)</label>
                                                <input
                                                    type="text"
                                                    placeholder="Code parrainage (optionnel)"
                                                    value={values.company_code}
                                                    onChange={(e) => handleInputChange('company_code', e.target.value)}
                                                />
                                                {errors.company_code && <span className="error-message">{errors.company_code}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Type catégorie */}
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>🏷️</span>
                                            <h3>Type catégorie</h3>
                                        </div>
                                        <div className="radio-group">
                                            {[
                                                { value: 'detaillant', label: 'Détaillant' },
                                                { value: 'grossiste', label: 'Grossiste' },
                                                { value: 'monoprix', label: 'Grande surface' },
                                            ].map((option) => (
                                                <label key={option.value} className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="categories"
                                                        value={option.value}
                                                        checked={values.categories === option.value}
                                                        onChange={(e) => setValues({ ...values, categories: e.target.value })}
                                                    />
                                                    <span className="radio-custom"></span>
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Type de règlement */}
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>💳</span>
                                            <h3>Type de règlement</h3>
                                        </div>
                                        <div className="radio-group">
                                            {[
                                                { value: 'espece', label: 'Espèce' },
                                                { value: 'traite', label: 'Traité' },
                                                { value: 'cheque', label: 'Chèque' },
                                            ].map((option) => (
                                                <label key={option.value} className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="regulation"
                                                        value={option.value}
                                                        checked={values.regulation === option.value}
                                                        onChange={(e) => setValues({ ...values, regulation: e.target.value })}
                                                    />
                                                    <span className="radio-custom"></span>
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Sécurité */}
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>🔒</span>
                                            <h3>Sécurité</h3>
                                        </div>
                                        <div className="input-group">
                                            <div className="input-field-wrapper">
                                                <label>Mot de passe <span className="required">*</span></label>
                                                <div className="password-wrapper">
                                                    <input
                                                        type={passwordVisible ? 'text' : 'password'}
                                                        placeholder="Mot de passe"
                                                        value={values.password}
                                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                                    />
                                                    <button type="button" onClick={togglePassword} className="eye-btn">
                                                        {passwordVisible ? <icons.BsEyeSlash /> : <icons.BsEye />}
                                                    </button>
                                                </div>
                                                {errors.password && <span className="error-message">{errors.password}</span>}
                                            </div>
                                            <div className="input-field-wrapper">
                                                <label>Confirmer le mot de passe <span className="required">*</span></label>
                                                <div className="password-wrapper">
                                                    <input
                                                        type={confirmPasswordVisible ? 'text' : 'password'}
                                                        placeholder="Confirmer le mot de passe"
                                                        value={values.confirmPassword}
                                                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                                                    />
                                                    <button type="button" onClick={toggleConfirmPassword} className="eye-btn">
                                                        {confirmPasswordVisible ? <icons.BsEyeSlash /> : <icons.BsEye />}
                                                    </button>
                                                </div>
                                                {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeSection === 'shops' && (
                                <div className="form-content-scroll">
                                    <div className="form-section-card">
                                        <div className="section-title">
                                            <span>🏪</span>
                                            <h3>Points de vente</h3>
                                        </div>

                                        <div className="shops-container">
                                            <button type="button" className="btn-add-shop" onClick={addField}>
                                                + Ajouter un magasin
                                            </button>

                                            {shopFields.map((field, index) => (
                                                <div key={field.id} className="shop-card">
                                                    <div className="shop-card-header">
                                                        <span className="shop-badge">Magasin {index + 1}</span>
                                                        {shopFields.length > 1 && (
                                                            <button type="button" className="remove-shop" onClick={() => confirmDeleteShop(field.id)}>
                                                                <icons.BsTrash />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="shop-card-body">
                                                        <div className="input-field-wrapper">
                                                            <label>Nom du magasin <span className="required">*</span></label>
                                                            <input
                                                                type="text"
                                                                name="name"
                                                                placeholder="Nom du magasin"
                                                                value={field.name}
                                                                onChange={(e) => handleChangeShopForm(field.id, e)}
                                                            />
                                                            {errors[`shop_${index}_name`] && <span className="error-message">{errors[`shop_${index}_name`]}</span>}
                                                        </div>

                                                        <div className="address-row">
                                                            <div className="input-field-wrapper">
                                                                <label>Numéro <span className="required">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    name="address_number"
                                                                    placeholder="Numéro"
                                                                    value={field.address_number}
                                                                    onChange={(e) => handleChangeShopForm(field.id, e)}
                                                                />
                                                                {errors[`shop_${index}_address_number`] && <span className="error-message">{errors[`shop_${index}_address_number`]}</span>}
                                                            </div>
                                                            <div className="input-field-wrapper">
                                                                <label>Rue <span className="required">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    name="street"
                                                                    placeholder="Rue"
                                                                    value={field.street}
                                                                    onChange={(e) => handleChangeShopForm(field.id, e)}
                                                                />
                                                                {errors[`shop_${index}_street`] && <span className="error-message">{errors[`shop_${index}_street`]}</span>}
                                                            </div>
                                                        </div>

                                                        <div className="address-row">
                                                            <div className="input-field-wrapper">
                                                                <label>Ville <span className="required">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    name="ville"
                                                                    placeholder="Ville"
                                                                    value={field.ville}
                                                                    onChange={(e) => handleChangeShopForm(field.id, e)}
                                                                />
                                                                {errors[`shop_${index}_ville`] && <span className="error-message">{errors[`shop_${index}_ville`]}</span>}
                                                            </div>
                                                            <div className="input-field-wrapper">
                                                                <label>Code postal <span className="required">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    name="postal_code"
                                                                    placeholder="Code postal"
                                                                    value={field.postal_code}
                                                                    onChange={(e) => handleChangeShopForm(field.id, e)}
                                                                />
                                                                {errors[`shop_${index}_postal_code`] && <span className="error-message">{errors[`shop_${index}_postal_code`]}</span>}
                                                            </div>
                                                        </div>

                                                        {/* Country Dropdown */}
                                                        <div className="input-field-wrapper">
                                                            <label>Pays <span className="required">*</span></label>
                                                            <select
                                                                name="country"
                                                                value={field.country || ''}
                                                                onChange={(e) => handleChangeShopForm(field.id, e)}
                                                            >
                                                                <option value="">Choisir un pays</option>
                                                                {countries.map((country, idx) => (
                                                                    <option key={idx} value={country.code}>
                                                                        {country.flag} {country.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {errors[`shop_${index}_country`] && <span className="error-message">{errors[`shop_${index}_country`]}</span>}
                                                        </div>

                                                        {/* Region Dropdown - Only shown if country is Tunisia (TUN) */}
                                                        {field.country === 'TUN' && (
                                                            <div className="input-field-wrapper">
                                                                <label>Gouvernorat <span className="required">*</span></label>
                                                                <select
                                                                    name="region"
                                                                    value={field.region || ''}
                                                                    onChange={(e) => handleChangeShopForm(field.id, e)}
                                                                >
                                                                    <option value="">Choisir un gouvernorat</option>
                                                                    {TunisiaCities.map((city, idx) => (
                                                                        <option key={idx} value={city.name}>
                                                                            {city.name}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {errors[`shop_${index}_region`] && <span className="error-message">{errors[`shop_${index}_region`]}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-actions">
                                {activeSection === 'shops' && (
                                    <button type="button" className="btn-back" onClick={() => setActiveSection('account')}>
                                        ← Retour
                                    </button>
                                )}
                                {activeSection === 'account' && (
                                    <button type="button" className="btn-next" onClick={handleContinue}>
                                        Continuer →
                                    </button>
                                )}
                                {activeSection === 'shops' && (
                                    <button type="submit" className="btn-submit" disabled={load}>
                                        {load ? <RotatingSquare color="#1A5D3A" size={20} /> : "S'inscrire"}
                                    </button>
                                )}
                            </div>
                        </form>

                        <Link className="home-link" to="/">
                            <span>←</span> Retour à l'accueil
                        </Link>

                        <div className="login-link">
                            Déjà un compte ? <Link to="/login">Se connecter</Link>
                        </div>
                    </div>
                </div>

                {/* Delete Modal */}
                {showDeleteModal && (
                    <div className="modal-delete" onClick={() => setShowDeleteModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-icon">🗑️</div>
                            <h3>Supprimer le magasin</h3>
                            <p>Cette action est irréversible</p>
                            <div className="modal-actions">
                                <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Annuler</button>
                                <button className="btn-confirm" onClick={() => {
                                    if (shopToDeleteIndex !== null) {
                                        handleDeleteAddedShop(shopToDeleteIndex);
                                    }
                                    setShowDeleteModal(false);
                                }}>Supprimer</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const RegisterPage = () => <PageWrapper><Register /></PageWrapper>;
export default RegisterPage;